import React, { useState, useRef, useEffect } from 'react';
import * as api from '../api/lookup';
import { useNavigation } from '../context/NavigationContext';
import { getItemHistory, getAvailableSizes } from '../api/inventory';
import { getAppointmentChangeLog } from '../api/appointments';
import { generateReceipt } from '../api/receipts';
import { downloadHtml } from '../utils/download';

let seq = 0;
const nid = () => ++seq;

// ── Presentational bits ──────────────────────────────────────────────────

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-gray-50 last:border-0">
      <span className="text-gray-400">{k}</span>
      <span className="text-gray-800 font-medium text-right">{v ?? '—'}</span>
    </div>
  );
}

function Chips({ options, onPick, disabled }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {options.map((o, i) => (
        <button key={i} disabled={disabled} onClick={() => onPick(o)}
          className="text-xs font-semibold bg-white border border-zoddle-green text-zoddle-green px-3 py-1.5 rounded-full hover:bg-zoddle-green hover:text-white transition disabled:opacity-50">
          {o.label}
        </button>
      ))}
    </div>
  );
}

function DrillRows({ items, renderLeft, renderRight, onPick, keyFn }) {
  return (
    <div className="space-y-1">
      {items.map(item => (
        <button key={keyFn(item)} onClick={() => onPick(item)}
          className="w-full text-left flex justify-between items-center px-2.5 py-1.5 rounded-lg hover:bg-gray-50 border border-gray-100 transition">
          {renderLeft(item)}{renderRight(item)}
        </button>
      ))}
    </div>
  );
}

function Bubble({ role, children }) {
  const isBot = role === 'bot';
  return (
    <div className={`flex ${isBot ? 'justify-start' : 'justify-end'} mb-3`}>
      <div className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm ${
        isBot ? 'bg-white border border-gray-200 text-gray-800' : 'bg-zoddle-green text-white'}`}>
        {children}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

const PHONE_OPTIONS = [
  { label: 'Appointment history', value: 'appointments' },
  { label: 'Order history', value: 'orders' },
  { label: 'Lifetime stats', value: 'summary' },
  { label: 'In flight now', value: 'inflight' },
];
const APPOINTMENT_OPTIONS = [
  { label: 'Full detail', value: 'detail' },
  { label: 'Order summary', value: 'order_summary' },
  { label: 'Change log', value: 'change_log' },
  { label: 'Status history', value: 'status_history' },
  { label: 'Generate Invoice', value: 'invoice' },
  { label: 'Handled by', value: 'handled_by' },
  { label: 'What should I do next?', value: 'next_action' },
];
const DESIGN_OPTIONS = [
  { label: 'Design details', value: 'details' },
  { label: 'Inventory count', value: 'inventory_count' },
  { label: 'Units sold', value: 'units_sold' },
  { label: 'Sell-through rate', value: 'sell_through' },
  { label: 'Stock by size', value: 'stock_by_size' },
];
const BARCODE_OPTIONS = [
  { label: 'Item detail + history', value: 'detail_history' },
  { label: 'Pricing', value: 'pricing' },
  { label: 'Sale info', value: 'sale_info' },
  { label: 'Recent appointments', value: 'recent_appts' },
  { label: 'Other sizes in stock', value: 'other_sizes' },
];

export default function Lookup() {
  const [thread, setThread] = useState([
    { id: nid(), role: 'bot', node: <p>Hi! Give me a phone number, Appointment UID, Design ID, or Zoddle Barcode.</p> },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread, thinking]);

  // ⌘K deep link: navigate('lookup', { query }) asks the question straight away.
  const { navData } = useNavigation();
  useEffect(() => {
    if (navData?.query) handleSend(navData.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navData]);

  function pushUser(text) { setThread(t => [...t, { id: nid(), role: 'user', node: text }]); }
  function pushBot(node) { setThread(t => [...t, { id: nid(), role: 'bot', node }]); }

  async function run(fn) {
    setThinking(true);
    try { await fn(); }
    catch (e) { pushBot(<p className="text-zoddle-pink">{e.response?.data?.detail || 'Something went wrong.'}</p>); }
    finally { setThinking(false); }
  }

  function resetPrompt() {
    pushBot(<p>Sure — give me a phone number, Appointment UID, Design ID, or Zoddle Barcode.</p>);
  }

  // ── Search / detect ──────────────────────────────────────────────────

  async function handleSend(raw) {
    const text = raw.trim();
    if (!text) return;
    pushUser(text);
    setInput('');
    await run(async () => handleResolved(await api.detect(text)));
  }

  function handleResolved(r) {
    if (r.type === 'not_found') {
      pushBot(<p>Couldn't find anything for that. Check the number, UID, barcode, or design ID.</p>);
      return;
    }
    if (r.type === 'ambiguous') {
      pushBot(
        <div>
          <p className="mb-1">That could be either of these — which did you mean?</p>
          <Chips options={r.candidates.map(c => ({ label: c.label, c }))} onPick={o => {
            pushUser(o.label);
            handleResolved({ type: o.c.type, resolved: { [o.c.type === 'design_id' ? 'design_code' : 'zoddle_barcode']: o.c.value } });
          }} />
        </div>
      );
      return;
    }
    if (r.type === 'phone') return showPhoneMenu(r.resolved.phone, r.resolved.customer_name);
    if (r.type === 'appointment_uid') return showAppointmentMenu(r.resolved.zcp_appointment_uid, r.resolved.customer_name);
    if (r.type === 'design_id') return showDesignMenu(r.resolved.design_code, r.resolved.design_name);
    if (r.type === 'zoddle_barcode') return showBarcodeMenu(r.resolved.zoddle_barcode);
  }

  // ── Phone ────────────────────────────────────────────────────────────

  function showPhoneMenu(phone, name) {
    pushBot(
      <div>
        <p className="font-semibold mb-2">{name ? `${name} · ` : ''}{phone}. What do you want to know?</p>
        <Chips options={[...PHONE_OPTIONS, { label: 'New search', value: 'new' }]}
          onPick={o => o.value === 'new' ? (pushUser('New search'), resetPrompt()) : askPhone(phone, o)} />
      </div>
    );
  }

  async function askPhone(phone, opt) {
    pushUser(opt.label);
    await run(async () => {
      if (opt.value === 'appointments') {
        const list = await api.phoneAppointments(phone);
        pushBot(list.length === 0 ? <p>No appointments.</p> : (
          <DrillRows items={list} keyFn={a => a.appointment_id}
            renderLeft={a => <span><span className="font-mono text-xs text-gray-500 mr-2">{a.zcp_appointment_uid || '—'}</span>{a.scheduled_date} · {a.scheduled_time}</span>}
            renderRight={a => <span className="text-xs font-semibold text-zoddle-blue">{a.operational_status}</span>}
            onPick={a => { pushUser(a.zcp_appointment_uid); showAppointmentMenu(a.zcp_appointment_uid); }} />
        ));
      } else if (opt.value === 'orders') {
        const list = await api.phoneOrders(phone);
        pushBot(list.length === 0 ? <p>No orders.</p> : (
          <DrillRows items={list} keyFn={o => o.order_history_id}
            renderLeft={o => <span><span className="font-mono text-xs text-gray-500 mr-2">{o.sheet_order_id}</span>{o.sale_date?.slice(0, 10)}</span>}
            renderRight={o => <span className="text-xs font-semibold text-zoddle-green">₹{o.gmv} · {o.status}</span>}
            onPick={o => o.appointment_uid && (pushUser(o.appointment_uid), showAppointmentMenu(o.appointment_uid))} />
        ));
      } else if (opt.value === 'summary') {
        const d = await api.phoneSummary(phone);
        pushBot(<div>
          <Row k="Orders" v={d.order_count} /><Row k="Total GMV" v={`₹${d.total_gmv}`} /><Row k="AOV" v={`₹${Math.round(d.aov)}`} />
          <Row k="Repeat customer" v={d.is_repeat ? 'Yes' : 'No'} /><Row k="Referral code" v={d.referral_code} />
          <Row k="Source" v={d.source_l2} /><Row k="Address" v={d.address} />
        </div>);
      } else if (opt.value === 'inflight') {
        const list = await api.phoneInFlight(phone);
        pushBot(list.length === 0 ? <p>Nothing in flight right now.</p> : (
          <div>{list.map(a => (
            <div key={a.appointment_id} className="mb-2 last:mb-0">
              <Row k={a.zcp_appointment_uid} v={a.operational_status} /><Row k="Dispatched at" v={a.dispatched_at} />
            </div>
          ))}</div>
        ));
      }
      pushBot(<p className="text-xs text-gray-400 mb-1">Anything else about {phone}?</p>);
      pushBot(<Chips options={[...PHONE_OPTIONS, { label: 'New search', value: 'new' }]}
        onPick={o => o.value === 'new' ? (pushUser('New search'), resetPrompt()) : askPhone(phone, o)} />);
    });
  }

  // ── Appointment ──────────────────────────────────────────────────────

  function showAppointmentMenu(uid, name) {
    pushBot(
      <div>
        <p className="font-semibold mb-2">{uid}{name ? ` · ${name}` : ''}. What do you want to know?</p>
        <Chips options={[...APPOINTMENT_OPTIONS, { label: 'New search', value: 'new' }]}
          onPick={o => o.value === 'new' ? (pushUser('New search'), resetPrompt()) : askAppointment(uid, o)} />
      </div>
    );
  }

  async function askAppointment(uid, opt) {
    pushUser(opt.label);
    await run(async () => {
      if (opt.value === 'detail') {
        const d = await api.appointmentDetail(uid);
        pushBot(<div>
          <Row k="Customer" v={d.customer_name} /><Row k="Phone" v={d.phone} />
          <Row k="Date / time" v={`${d.scheduled_date} · ${d.scheduled_time}`} /><Row k="Address" v={d.address_line_1} />
          <Row k="Operational status" v={d.operational_status} /><Row k="Customer status" v={d.customer_status} />
          <Row k="Notes" v={d.notes} />
          {(d.children || []).map(c => <Row key={c.appointment_child_id} k="Child" v={`${c.gender} · size ${c.size}`} />)}
        </div>);
      } else if (opt.value === 'order_summary') {
        const d = await api.appointmentOrderSummary(uid);
        pushBot(d.orders.length === 0 ? <p>No order on file.</p> : (
          <div>{d.orders.map((o, i) => (
            <div key={i} className="mb-3 pb-3 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
              <Row k="Order" v={o.sheet_order_id} /><Row k="Status" v={o.status} /><Row k="GMV" v={`₹${o.gmv}`} />
              <Row k="Revenue" v={`₹${o.revenue}`} /><Row k="Gross margin" v={`₹${o.gross_margin}`} />
              <Row k="Items sold" v={o.item_count} /><Row k="Barcodes" v={o.zoddle_barcodes_sold.join(', ')} />
              <Row k="Brands" v={o.brands_sold.join(', ')} /><Row k="Rider" v={o.executive} /><Row k="Discount code" v={o.discount_code} />
            </div>
          ))}</div>
        ));
      } else if (opt.value === 'change_log') {
        const d = await api.appointmentDetail(uid);
        const logs = await getAppointmentChangeLog(d.appointment_id);
        pushBot(logs.length === 0 ? <p>No manual edits logged.</p> : (
          <div>{logs.map(l => <Row key={l.change_id} k={`${l.field_changed}: ${l.old_value ?? '—'} → ${l.new_value ?? '—'}`} v={l.changed_at} />)}</div>
        ));
      } else if (opt.value === 'status_history') {
        pushBot(
          <div>
            <p className="mb-1">Brief or detailed?</p>
            <Chips options={[{ label: 'Brief', value: 'brief' }, { label: 'Detailed', value: 'detailed' }]}
              onPick={async o => {
                pushUser(o.label);
                await run(async () => {
                  const rows = await api.appointmentStatusHistory(uid, o.value);
                  pushBot(rows.length === 0 ? <p>No entries.</p> : (
                    <div>{rows.map((r, i) => (
                      <Row key={i} k={r.milestone || `${r.field_changed}: ${r.old_value ?? '—'} → ${r.new_value ?? '—'}`} v={r.at || r.changed_at} />
                    ))}</div>
                  ));
                  pushBot(<p className="text-xs text-gray-400 mb-1">Anything else about {uid}?</p>);
                  pushBot(<Chips options={[...APPOINTMENT_OPTIONS, { label: 'New search', value: 'new' }]}
                    onPick={o2 => o2.value === 'new' ? (pushUser('New search'), resetPrompt()) : askAppointment(uid, o2)} />);
                });
              }} />
          </div>
        );
      } else if (opt.value === 'invoice') {
        const { html, filename } = await generateReceipt(uid);
        downloadHtml(html, filename);
        pushBot(<p>✓ Downloaded {filename}</p>);
      } else if (opt.value === 'handled_by') {
        const d = await api.appointmentHandledBy(uid);
        pushBot(<div>
          <Row k="Packed by" v={d.packed_by} /><Row k="Dispatched by" v={d.dispatched_by} /><Row k="Reversal handled by" v={d.reversal_handled_by} />
        </div>);
      } else if (opt.value === 'next_action') {
        const d = await api.appointmentNextAction(uid);
        pushBot(<p className="font-semibold">{d.next_action}</p>);
      }
      if (opt.value !== 'status_history') {
        pushBot(<p className="text-xs text-gray-400 mb-1">Anything else about {uid}?</p>);
        pushBot(<Chips options={[...APPOINTMENT_OPTIONS, { label: 'New search', value: 'new' }]}
          onPick={o => o.value === 'new' ? (pushUser('New search'), resetPrompt()) : askAppointment(uid, o)} />);
      }
    });
  }

  // ── Design ───────────────────────────────────────────────────────────

  function showDesignMenu(code, name) {
    pushBot(
      <div>
        <p className="font-semibold mb-2">Design {code}{name ? ` · ${name}` : ''}. What do you want to know?</p>
        <Chips options={[...DESIGN_OPTIONS, { label: 'New search', value: 'new' }]}
          onPick={o => o.value === 'new' ? (pushUser('New search'), resetPrompt()) : askDesign(code, o)} />
      </div>
    );
  }

  async function askDesign(code, opt) {
    pushUser(opt.label);
    await run(async () => {
      if (opt.value === 'details') {
        const d = await api.designDetails(code);
        pushBot(<div>
          {d.image_url_1 && <img src={d.image_url_1} alt="" className="w-20 h-20 object-cover rounded-lg mb-2 bg-gray-100" />}
          <Row k="Name" v={d.design_name} /><Row k="Brand" v={d.brand_name} /><Row k="Category" v={d.category} />
          <Row k="Gender" v={d.gender} /><Row k="Use case" v={d.use_case_tags} /><Row k="HSN" v={d.hsn_code} />
        </div>);
      } else if (opt.value === 'inventory_count') {
        const d = await api.designInventoryCount(code);
        pushBot(<div><Row k="Active (sellable)" v={d.active_count} /><Row k="Total inbounded" v={d.total_inbounded} /></div>);
      } else if (opt.value === 'units_sold') {
        pushBot(
          <div>
            <p className="mb-1">Which range?</p>
            <Chips options={[['today', 'Today'], ['week', 'Last Week'], ['month', 'Last Month'], ['lifetime', 'Lifetime']].map(([v, l]) => ({ label: l, value: v }))}
              onPick={async o => {
                pushUser(o.label);
                await run(async () => {
                  const d = await api.designUnitsSold(code, o.value);
                  pushBot(<Row k={o.label} v={`${d.units_sold} units`} />);
                  pushBot(<p className="text-xs text-gray-400 mb-1">Anything else about design {code}?</p>);
                  pushBot(<Chips options={[...DESIGN_OPTIONS, { label: 'New search', value: 'new' }]}
                    onPick={o2 => o2.value === 'new' ? (pushUser('New search'), resetPrompt()) : askDesign(code, o2)} />);
                });
              }} />
          </div>
        );
      } else if (opt.value === 'sell_through') {
        const d = await api.designSellThrough(code);
        pushBot(<div>
          <Row k="Sold" v={d.sold} /><Row k="Sendouts" v={d.sendouts} />
          <Row k="Sell-through" v={d.sell_through_pct != null ? `${d.sell_through_pct}%` : '—'} />
        </div>);
      } else if (opt.value === 'stock_by_size') {
        const rows = await api.designStockBySize(code);
        pushBot(<div>{rows.map(r => <Row key={r.size} k={r.size} v={r.active_count} />)}</div>);
      }
      if (opt.value !== 'units_sold') {
        pushBot(<p className="text-xs text-gray-400 mb-1">Anything else about design {code}?</p>);
        pushBot(<Chips options={[...DESIGN_OPTIONS, { label: 'New search', value: 'new' }]}
          onPick={o => o.value === 'new' ? (pushUser('New search'), resetPrompt()) : askDesign(code, o)} />);
      }
    });
  }

  // ── Barcode ──────────────────────────────────────────────────────────

  function showBarcodeMenu(barcode) {
    pushBot(
      <div>
        <p className="font-semibold mb-2 font-mono">{barcode}. What do you want to know?</p>
        <Chips options={[...BARCODE_OPTIONS, { label: 'New search', value: 'new' }]}
          onPick={o => o.value === 'new' ? (pushUser('New search'), resetPrompt()) : askBarcode(barcode, o)} />
      </div>
    );
  }

  async function loadEventPage(barcode, offset) {
    const d = await getItemHistory(barcode, { limit: 20, offset });
    pushBot(
      <div>
        {d.events.length === 0 ? <p>No more events.</p> : d.events.map(e => (
          <Row key={e.event_id} k={`${e.event_type}${e.field_name ? ` · ${e.field_name}` : ''}`} v={e.created_at} />
        ))}
        {d.has_more && (
          <Chips options={[{ label: 'Load 20 more', value: 'more' }]}
            onPick={async () => { pushUser('Load 20 more'); await run(() => loadEventPage(barcode, offset + 20)); }} />
        )}
      </div>
    );
  }

  async function askBarcode(barcode, opt) {
    pushUser(opt.label);
    await run(async () => {
      if (opt.value === 'detail_history') {
        const d = await getItemHistory(barcode, { limit: 20, offset: 0 });
        pushBot(<div>
          <Row k="Design" v={`${d.item.design_name} (${d.item.design_code})`} /><Row k="Brand" v={d.item.brand_name} />
          <Row k="Size" v={d.item.size} /><Row k="Status" v={d.item.current_status} /><Row k="Condition" v={d.item.current_condition} />
          <Row k="Location" v={d.item.current_location_label} />
          <Row k="Ownership" v={`${d.item.ownership_type} (orig. ${d.item.original_ownership_type})`} /><Row k="HSN" v={d.item.hsn_code} />
        </div>);
        pushBot(
          <div>
            {d.events.map(e => <Row key={e.event_id} k={`${e.event_type}${e.field_name ? ` · ${e.field_name}` : ''}`} v={e.created_at} />)}
            {d.has_more && (
              <Chips options={[{ label: 'Load 20 more', value: 'more' }]}
                onPick={async () => { pushUser('Load 20 more'); await run(() => loadEventPage(barcode, 20)); }} />
            )}
          </div>
        );
      } else if (opt.value === 'pricing') {
        const d = await getItemHistory(barcode, { limit: 1 });
        pushBot(
          <div>
            <Row k="Zoddle price" v={`₹${d.item.zoddle_price}`} /><Row k="Saver price" v={`₹${d.item.saver_price}`} />
            <Row k="MRP" v={`₹${d.item.mrp}`} /><Row k="Cost price" v={`₹${d.item.cost_price}`} />
            <Chips options={[{ label: 'See price change history', value: 'ph' }]}
              onPick={async () => {
                pushUser('See price change history');
                await run(async () => {
                  const history = await api.barcodePriceHistory(barcode);
                  pushBot(history.length === 0 ? <p>No price changes logged.</p> : (
                    <div>{history.map((h, i) => (
                      <Row key={i} k={`${h.change_type}: ₹${h.old_zoddle_price} → ₹${h.new_zoddle_price}`} v={h.price_updated_at} />
                    ))}</div>
                  ));
                });
              }} />
          </div>
        );
      } else if (opt.value === 'sale_info') {
        const d = await api.barcodeSaleInfo(barcode);
        pushBot(!d.sold ? <p>Not sold.</p> : !d.order_found ? <p>Sold, but no matching order found.</p> : (
          <div>
            <Row k="Order" v={d.sheet_order_id} /><Row k="Customer" v={`${d.customer_name} (${d.customer_phone})`} />
            <Row k="Sale date" v={d.sale_date} /><Row k="Sold price" v={`₹${d.sold_price}`} />
          </div>
        ));
      } else if (opt.value === 'recent_appts') {
        const list = await api.barcodeRecentAppointments(barcode);
        pushBot(list.length === 0 ? <p>Never sent out.</p> : (
          <div>{list.map((a, i) => <Row key={i} k={a.zcp_appointment_uid} v={a.sent_at} />)}</div>
        ));
      } else if (opt.value === 'other_sizes') {
        const d = await getAvailableSizes(barcode);
        pushBot(<div>{d.sizes.map(s => <Row key={s.size} k={`${s.size}${s.is_queried_size ? ' (this one)' : ''}`} v={`${s.available_count} available`} />)}</div>);
      }
      pushBot(<p className="text-xs text-gray-400 mb-1">Anything else about {barcode}?</p>);
      pushBot(<Chips options={[...BARCODE_OPTIONS, { label: 'New search', value: 'new' }]}
        onPick={o => o.value === 'new' ? (pushUser('New search'), resetPrompt()) : askBarcode(barcode, o)} />);
    });
  }

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-2xl mx-auto flex flex-col" style={{ height: 'calc(100vh - 90px)' }}>
      <div className="mb-3">
        <h1 className="text-2xl font-black text-gray-800">Lookup</h1>
        <p className="text-sm text-gray-400 mt-0.5">Phone number, Appointment UID, Design ID, or Zoddle Barcode</p>
      </div>

      <div className="flex-1 overflow-y-auto pr-1">
        {thread.map(m => <Bubble key={m.id} role={m.role}>{m.node}</Bubble>)}
        {thinking && (
          <Bubble role="bot"><span className="text-gray-400">···</span></Bubble>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2 pt-3 border-t border-gray-100 mt-2">
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend(input)}
          placeholder="Type a phone number, UID, barcode, or design ID..."
          className="flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
        <button onClick={() => handleSend(input)} disabled={thinking || !input.trim()}
          className="bg-zoddle-green text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:opacity-90 disabled:opacity-50 transition">
          Send
        </button>
      </div>
    </div>
  );
}
